import React from 'react';

import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';

import { useGetPromoMigrations } from '~/hooks/apis/promo/useGetPromoMigrations';

import { formatDateWithClock } from '~/services/TimeService';

function getErrorMessage(error: unknown): string {
  return error instanceof Error ? error.message : 'Unable to load svc-promo migrations.';
}

export default function PromoMigrations() {
  const { data, error, isFetching, isSuccess, refetch } = useGetPromoMigrations({ enabled: false });
  const migrations = data?.results.schemaVersion ?? [];

  return (
    <Box mt={4}>
      <Stack spacing={2}>
        <Box>
          <Typography variant="h6">svc-promo migrations</Typography>
          <Typography variant="body2" color="text.secondary">
            Load the ten most recently recorded migrations from the active svc-promo database.
          </Typography>
        </Box>

        <Box>
          <Button variant="contained" onClick={() => refetch()} disabled={isFetching}>
            {isFetching ? 'Loading migrations…' : 'Load migrations'}
          </Button>
        </Box>

        {isFetching && <CircularProgress size={24} aria-label="Loading svc-promo migrations" />}

        {error && <Alert severity="error">{getErrorMessage(error)}</Alert>}

        {isSuccess && !isFetching && (
          <TableContainer component={Paper} variant="outlined">
            <Table size="small" aria-label="Latest svc-promo migrations">
              <TableHead>
                <TableRow>
                  <TableCell>Version</TableCell>
                  <TableCell>Migration</TableCell>
                  <TableCell>Run at</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {migrations.map((migration) => (
                  <TableRow key={String(migration.id)}>
                    <TableCell>{migration.id}</TableCell>
                    <TableCell>{migration.name}</TableCell>
                    <TableCell>{formatDateWithClock(migration.timestamp)}</TableCell>
                  </TableRow>
                ))}
                {migrations.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={3}>No recorded migrations were returned.</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Stack>
    </Box>
  );
}
